<template>
  <VChart autoresize class="chart" :option="option" />
</template>

<script>
import { defineComponent, computed } from 'vue'
import VChart from 'vue-echarts'
import { use } from 'echarts/core'
import { CanvasRenderer } from 'echarts/renderers'
import { LineChart } from 'echarts/charts'
import { GridComponent, TooltipComponent, LegendComponent } from 'echarts/components'
import { detection_zone_palettes } from '@/constants.js'
import { useI18n } from 'vue-i18n'
import { currentLanguage } from '@/i18n'
import { formatPercent } from '@/i18n/format'

use([CanvasRenderer, LineChart, GridComponent, TooltipComponent, LegendComponent])

export default defineComponent({
  name: 'ChartDurationSegmentationPercentPerDay',
  components: { VChart },
  props: {
    durations: { type: Object, required: true },
    timeSpan: { type: String, required: true },
    detectionZoneTypeName: { type: String, required: true }
  },
  setup(props){
    const labels = computed(() => Object.keys(props.durations || {}))
    const zoneName = computed(() => {
      const ls = labels.value
      return ls.length > 0 ? Object.keys(props.durations[ls[0]] || {})[0] : null
    })

    const pctFor = (get) => computed(() =>
      labels.value.map(date => {
        const data = props.durations[date][zoneName.value] || {}
        const w = data.walk_bys || 0
        const s = data.short_lingerers || 0
        const c = data.clear_lingerers || 0
        const total = w + s + c
        return total > 0 ? get({w,s,c})/total*100 : 0
      })
    )

    const walkPct   = pctFor(({w})=>w)
    const shortPct  = pctFor(({s})=>s)
    const clearPct  = pctFor(({c})=>c)

    const palette = computed(() =>
      detection_zone_palettes[props.detectionZoneTypeName] ?? {
        segmentation: { walkBy:'#B2C149', shortLinger:'#4F7EB3', clearLinger:'#425461' }
      }
    )

    const { t } = useI18n()
    // English keeps its en-GB "Mon 17 Oct"; Dutch reads "ma 17 okt".
    const fmtX = v => {
      const d = new Date(v)
      return d.toLocaleDateString(currentLanguage() === 'en' ? 'en-GB' : 'nl', { weekday:'short', day:'numeric', month:'short' })
    }

    const option = computed(() => ({
      tooltip: {
        trigger:'axis',
        axisPointer:{ type:'cross', label:{ backgroundColor:'#6a7985' } },
        formatter: params => params.map(p => `${p.seriesName}: ${currentLanguage() === 'en' ? `${p.value.toFixed(1)}%` : formatPercent(p.value, { decimals: 1 })}`).join('<br>')
      },
      legend: { bottom:0 },
      grid: { bottom:'10%', containLabel:true },
      xAxis: { type:'category', boundaryGap:false, data:labels.value, axisLabel:{ formatter:fmtX, rotate:45 } },
      yAxis: { type:'value', name: t('charts.perDay.visitorPctAxis', { span: props.timeSpan }), nameTextStyle:{ align:'left' }, max:100, axisLabel:{ formatter:v=>`${v}%` } },
      series: [
        { name:t('charts.segments.walkBysPct'),        type:'line', stack:'pct', data:walkPct.value,  areaStyle:{}, emphasis:{ focus:'series' }, itemStyle:{ color:palette.value.segmentation.walkBy } },
        { name:t('charts.segments.shortLingerersPct'), type:'line', stack:'pct', data:shortPct.value, areaStyle:{}, emphasis:{ focus:'series' }, itemStyle:{ color:palette.value.segmentation.shortLinger } },
        { name:t('charts.segments.clearLingerersPct'), type:'line', stack:'pct', data:clearPct.value, areaStyle:{}, emphasis:{ focus:'series' }, itemStyle:{ color:palette.value.segmentation.clearLinger } },
      ]
    }))

    return { option }
  }
})
</script>

<style scoped>
.chart{ width:100%; height:100%; }
</style>